// Response-rule fields split out of connectorConfig.js to keep that file under the 300-line
// ceiling. connectorConfig.js imports from here, so this module stays self-contained: the
// placeholder check travels in as a function rather than being imported back.
export const MAX_FILTER_ROWS = 3;

// The rule compares with Object.is on the server, so [], 0, false and null are each a value a
// reader has to be able to enter; the field holds the JSON text and a bare word stays a string.
export const parseEquals = (text) => {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
};

// JSON.stringify(undefined) is undefined rather than a string, so notFoundWhen's path-only rule —
// a path that resolves to nothing, which the form supports deliberately — would leave the field
// uncontrolled and make its own .trim() throw.
export const equalsToText = (value) => (value === undefined ? '' : JSON.stringify(value));

// A stored errorWhen can carry a path with neither equals nor notEquals beside it. The form has no
// third mode for that shape, so it travels as errorWhenHasComparison and is written back as it
// arrived: a reader who only changed the timeout does not have to invent a comparison for a rule
// they never touched. A rule being authored here still needs a value — see responseRuleErrors —
// and typing one turns this off, which is why both halves read the same predicate.
export const errorWhenIsPathOnly = (form) =>
  form.errorWhenHasComparison === false && (form.errorWhenValue ?? '').trim() === '';

// strict is optional on the server and off means today's fallback to every row, so only a
// checked box is written: a stored rule without it saves back without it.
export const filterToRows = (filter) =>
  (filter || []).map((rule) => ({ key: rule.path, value: String(rule.equals ?? ''), strict: rule.strict === true }));

// notFoundWhen, filter and errorWhen, in the order the server's schema reads them.
export const responseRules = (form) => {
  const rules = {};

  const notFoundPath = form.notFoundPath.trim();
  if (notFoundPath) {
    rules.notFoundWhen = { path: notFoundPath };
    // Left blank the rule matches a path that resolves to nothing, which is a different rule
    // from matching the empty string.
    if (form.notFoundEquals !== '') rules.notFoundWhen.equals = parseEquals(form.notFoundEquals);
  }

  // The server matches a filter rule case-insensitively on the text of the response value, so
  // this one rule is string-only and takes no JSON literal the way the other two do.
  const filter = (form.filterRows || [])
    .filter((row) => row.key.trim())
    .map((row) => {
      const rule = { path: row.key.trim(), equals: row.value.trim() };
      if (row.strict) rule.strict = true;
      return rule;
    });
  if (filter.length) rules.filter = filter;

  const errorWhenPath = form.errorWhenPath.trim();
  if (errorWhenPath && errorWhenIsPathOnly(form)) {
    rules.errorWhen = { path: errorWhenPath };
  } else if (errorWhenPath) {
    const value = parseEquals(form.errorWhenValue);
    rules.errorWhen =
      form.errorWhenMode === 'notEquals'
        ? { path: errorWhenPath, notEquals: value }
        : { path: errorWhenPath, equals: value };
  }

  return rules;
};

// unknownMessageIn returns the message for the first placeholder the server cannot fill in, or a
// falsy value when every placeholder in the text is one it can.
export const responseRuleErrors = (form, unknownMessageIn) => {
  const errors = {};
  const rows = form.filterRows || [];

  const badFilterPath = rows.find((row) => !row.key.trim() && row.value.trim());
  const unknownFilterValue = rows.map((row) => unknownMessageIn(row.value)).find(Boolean);
  const kept = rows.filter((row) => row.key.trim()).length;
  if (badFilterPath) errors.filter = 'Every filter rule needs a response path.';
  else if (unknownFilterValue) errors.filter = unknownFilterValue;
  // The Add button stops at the cap, but a config that arrives from the API over it would
  // otherwise be saved back over it without a word.
  else if (kept > MAX_FILTER_ROWS) errors.filter = `At most ${MAX_FILTER_ROWS} filter rules.`;

  const errorWhenPath = form.errorWhenPath.trim();
  const errorWhenValue = (form.errorWhenValue ?? '').trim();
  // A rule that arrived from the server with no comparison at all is written back as it arrived,
  // so it is not a rule this form is authoring and needs no value invented for it.
  if (errorWhenPath && errorWhenValue === '' && !errorWhenIsPathOnly(form))
    errors.errorWhenValue = 'A value is required once a path is set.';
  // responseRules gates the whole rule on the path, so a value typed without one is dropped on
  // Save: without this the connector saves clean with no error rule and nothing on screen.
  else if (!errorWhenPath && errorWhenValue !== '') errors.errorWhenPath = 'A value needs a response path.';

  return errors;
};
